import { Button } from '@repo/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import { DataProvider } from '@repo/ui/dataProvider'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Language } from '@repo/ui/lang'
import { LanguageSelector } from '@repo/ui/language-selector'
import { Globe, Info } from 'lucide-react'
import { useMemo } from 'react'

import { RootState } from '@/store'
import { adminStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { OrganizationFields } from '@/store/spi/api'
import { Organization } from '@/store/user'
import { organizationSchema } from '@/validation-schemas'

export function AccountRoute({ organization }: { organization: Organization }) {
  const dispatch = useStoreDispatch()
  const { t, tc } = useT({ keyPrefix: 'admin.organization' })

  const setOrganizationLanguage = (language: Language) => {
    void dispatch(
      adminStoreActions.organization.update({
        fields: { language },
      }),
    )
  }

  const defaultValues = useMemo(
    () => ({
      name: organization.name,
      url: organization.url,
    }),
    [organization],
  )

  return (
    <Card className="flex flex-1 flex-col overflow-hidden overflow-y-auto">
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>

      <OrganizationForm defaultValues={defaultValues} />

      <div className="flex select-none items-center gap-2 px-6 pb-4">
        <Globe className="size-5" />
        {t('language')}
        <LanguageSelector
          defaultLanguage={organization.language}
          onSetLanguage={setOrganizationLanguage}
        />
      </div>
      <DataProvider
        selectors={[(state: RootState) => state.user.caseWorker]}
        render={(caseWorker) => (
          <div className="flex select-none items-center gap-2 px-6 pb-6">
            <Info className="size-5" /> {t('personalLanguage')}
            <span className="font-semibold">
              {tc(caseWorker?.language || organization.language)}
            </span>
          </div>
        )}
      />
    </Card>
  )
}

function OrganizationForm({
  defaultValues,
}: {
  defaultValues: {
    name: string
    url?: string
  }
}) {
  const { tc } = useT()
  const dispatch = useStoreDispatch()

  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues,
    onSubmit: (values) => {
      const fields: OrganizationFields = {}
      if (values.name !== defaultValues.name) fields.name = values.name
      if (values.url !== defaultValues.url) fields.url = values.url
      if (Object.keys(fields).length === 0) return
      void dispatch(adminStoreActions.organization.update({ fields }))
    },
    schema: {
      name: organizationSchema.name,
      url: organizationSchema.url,
    },
  })

  return (
    <Form {...form}>
      <form onSubmit={submit} onKeyDown={submitOnPressMetaEnter} className="flex flex-col">
        <CardContent className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tc('name')}</FormLabel>
                <FormControl>
                  <Input placeholder={tc('name')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{'url'.toLocaleUpperCase()}</FormLabel>
                <FormControl>
                  <Input placeholder={'https://'} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>

        <CardFooter className="justify-end">
          <Button type="submit" disabled={!isFormValid}>
            {tc('update', { cfl: true })}
          </Button>
        </CardFooter>
      </form>
    </Form>
  )
}
